import { appNotificationsSliceActions } from '@src/providers/AppNotifications/redux/AppNotificationsSlice';
import { selectActiveNotificationCount } from '@src/providers/AppNotifications/redux/selectors';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { Box, IconButton, IconName, IndicatorDotVariants, IndicatorDotWrapper, MobileHeader } from '@talos/kyoko';
import { useCallback, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { MOBILE_HEADER_TOP_OFFSET_DEFAULT, MOBILE_HEADER_TOP_OFFSET_PROPERTY } from '../mobileHeaderOffset';
import { MobileNavMenu } from './MobileNavMenu';
import { MOBILE_PWA_INSTALL_BANNER_HEIGHT, MobilePwaInstallBanner } from './MobilePwaInstallBanner';

const { openInbox } = appNotificationsSliceActions;

interface MobileAppHeaderProps {
  showNavMenu?: boolean;
}

export function MobileAppHeader({ showNavMenu = true }: MobileAppHeaderProps) {
  const dispatch = useAppStateDispatch();
  const activeNotificationsCount = useAppStateSelector(selectActiveNotificationCount) ?? 0;
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  const handleOpenNotifications = useCallback(() => {
    dispatch(openInbox());
  }, [dispatch]);

  return (
    <>
      <MobileHeaderTopOffset $hasInstallBanner={showInstallBanner} />
      <MobilePwaInstallBanner onVisibilityChange={setShowInstallBanner} />
      <MobileHeaderOffset>
        <MobileHeader
          toolbarContent={
            <IndicatorDotWrapper
              show={activeNotificationsCount > 0}
              variant={IndicatorDotVariants.Notification}
              inset={6}
              size={8}
            >
              <IconButton
                ghost
                round
                icon={IconName.Bell}
                onClick={handleOpenNotifications}
                data-testid="mobile-header-notifications-button"
              />
            </IndicatorDotWrapper>
          }
        />
      </MobileHeaderOffset>
      {showNavMenu ? <MobileNavMenu /> : null}
      {showInstallBanner ? <Box h={`${MOBILE_PWA_INSTALL_BANNER_HEIGHT}px`} flexShrink={0} /> : null}
    </>
  );
}

const MobileHeaderTopOffset = createGlobalStyle<{ $hasInstallBanner: boolean }>`
  :root {
    ${MOBILE_HEADER_TOP_OFFSET_PROPERTY}: ${({ $hasInstallBanner }) =>
      $hasInstallBanner
        ? `calc(${MOBILE_HEADER_TOP_OFFSET_DEFAULT} + ${MOBILE_PWA_INSTALL_BANNER_HEIGHT}px)`
        : MOBILE_HEADER_TOP_OFFSET_DEFAULT};
  }
`;

const MobileHeaderOffset = styled.div`
  display: contents;

  & > * {
    top: var(${MOBILE_HEADER_TOP_OFFSET_PROPERTY}, ${MOBILE_HEADER_TOP_OFFSET_DEFAULT});
  }
`;
